import React, { Suspense } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import Navbar from "../components/navbar/Navbar";
import PrivateRoute from "./PrivateRoute";
import { isCampaignActive } from "../config/campaign";

// Keep LoginPage as static import (entry route)
import LoginPage from "../pages/LoginPage/LoginPage";

// Lazy-load all other routes for code splitting
const Home = React.lazy(() => import("../pages/Home/HomePage"));
const Events = React.lazy(() => import("../pages/Events/Events"));
const Profil = React.lazy(() => import("../pages/Profil/Profil"));
const Shop = React.lazy(() => import("../pages/Shop/Shop"));
const ARCamera = React.lazy(() => import("../pages/ARCamera/ARCamera"));
const ProductDetail = React.lazy(() => import("../pages/Shop/ProductDetail/ProductDetail"));
const ProductDetailCustomBox = React.lazy(() => import("../pages/Shop/ProductDetail/ProductDetailCustomBox/ProductDetailCustomBox"));
const EventDetails = React.lazy(() => import("../pages/EventDetails/EventDetails"));
const NewsDetails = React.lazy(() => import("../pages/NewsDetails/NewsDetails"));
const ShoppingCart = React.lazy(() => import("../pages/Shop/ShoppingCart/ShoppingCart"));
const ShipmentAddress = React.lazy(() => import("../pages/Shop/ShipmentAddress/ShipmentAddress"));
const OrderReview = React.lazy(() => import("../pages/Shop/OrderReview/OrderReview"));
const OrderDone = React.lazy(() => import("../pages/Shop/OrderDone/OrderDone"));
const CodeEingabe = React.lazy(() => import("../pages/CodeEingabe/CodeEingabe"));
const CodeInputDone = React.lazy(() => import("../pages/CodeEingabe/CodeInputDone/CodeInputDone"));
const ProductWinPage = React.lazy(() => import("../pages/Profil/ProductWinPage/ProductWinPage"));
const CollectPoints = React.lazy(() => import("../pages/OnBoarding/CollectPoints/CollectPoints"));
const PersonalSpace = React.lazy(() => import("../pages/Profil/PersonalSpacePage/PersonalSpace"));
const OrderHistory = React.lazy(() => import("../pages/Profil/OrderHistory/OrderHistory"));
const FAQ = React.lazy(() => import("../pages/FAQ/FAQ"));
const AccountSetup = React.lazy(() => import("../pages/Home/AccountSetup/AccountSetup"));
const UsernamePage = React.lazy(() => import("../pages/LoginPage/Username/Username.page"));
const EmailRegistration = React.lazy(() => import("../pages/EmailRegistration/EmailRegistration"));
const EmailLogin = React.lazy(() => import("../pages/EmailRegistration/EmailLogin"));
const EmailForgotPassword = React.lazy(() => import("../pages/EmailRegistration/EmailForgotPassword"));
const EmailRegistrationDone = React.lazy(() => import("../pages/EmailRegistration/EmailRegistrationDone"));
const ConfirmEmail = React.lazy(() => import("../pages/EmailRegistration/ConfirmEmail"));
const PasswordRecovery = React.lazy(() => import("../pages/PasswordRecovery/PasswordRecovery"));
const Datenschutz = React.lazy(() => import("../pages/Datenschutz/Datenschutz"));
const DatenschutzPublic = React.lazy(() => import("../pages/DatenschutzPublic/DatenschutzPublic"));
const Impressum = React.lazy(() => import("../pages/Impressum/Impressum"));
const Teilnahmebedingung = React.lazy(() => import("../pages/Teilnahmebedingung/Teilnahmebedingung"));
const TeilnahmebedingungPublic = React.lazy(() => import("../pages/TeilnahmebedingungPublic/TeilnahmebedingungPublic"));
const NotFound = React.lazy(() => import("../pages/NotFound/NotFound"));

const LoadingFallback = () => (
  <div style={{ backgroundColor: "#005e34", minHeight: "100vh", minWidth: "100vw" }} />
);

interface MohrenRouterProps {}

const MohrenRouter = ({}: MohrenRouterProps) => {
  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Switch>
          {/** all can access this pages */}
          <Route exact path="/" component={LoginPage} />
          <Route exact path="/apple" component={LoginPage} />
          <Route path="/emailregistration" component={EmailRegistration} />
          <Route path="/emaillogin" component={EmailLogin} />
          <Route path="/forgotpw" component={EmailForgotPassword} />
          <Route path="/password-recovery" component={PasswordRecovery} />
          <Route path="/agb" component={TeilnahmebedingungPublic} />
          <Route
            path="/emailregistrationdone"
            component={EmailRegistrationDone}
          />
          <Route path="/auth/confirm-email" component={ConfirmEmail} />
          {/** only auth user can access */}
          <PrivateRoute path="/home" component={Home} />
          <PrivateRoute path="/events" component={Events} />
          <PrivateRoute path="/profil" component={Profil} />
          <PrivateRoute
            path="/teilnahmebedingung"
            component={Teilnahmebedingung}
          />
          <PrivateRoute path="/impressum" component={Impressum} />
          <Route path="/privacy" component={DatenschutzPublic} />
          <PrivateRoute path="/datenschutz" component={Datenschutz} />
          <PrivateRoute path="/orderhistory" component={OrderHistory} />
          <PrivateRoute path="/username" component={UsernamePage} />
          <PrivateRoute path="/eventdetails" component={EventDetails} />
          <PrivateRoute path="/accountsetup" component={AccountSetup} />
          <PrivateRoute path="/personalspace" component={PersonalSpace} />
          <PrivateRoute path="/ar-camera" component={ARCamera} />
          <PrivateRoute path="/shoppingcart" component={ShoppingCart} />
          <PrivateRoute path="/store" component={Shop} />
          <PrivateRoute path="/product/:productId" component={ProductDetail} />
          <PrivateRoute path="/newsdetail/:newsId" component={NewsDetails} />
          <PrivateRoute path="/faq" component={FAQ} />
          <PrivateRoute
            path="/customproduct/:productId"
            component={ProductDetailCustomBox}
          />

          <PrivateRoute path="/wonpickups" component={ProductWinPage} />
          <PrivateRoute path="/collectpoints" component={CollectPoints} />
          {/* Campaign-gated routes: redirect home when the campaign is inactive. */}
          {isCampaignActive() ? (
            <PrivateRoute path="/codeinput" component={CodeEingabe} />
          ) : (
            <Route path="/codeinput" render={() => <Redirect to="/home" />} />
          )}
          {isCampaignActive() ? (
            <PrivateRoute path="/codeinputdone" component={CodeInputDone} />
          ) : (
            <Route path="/codeinputdone" render={() => <Redirect to="/home" />} />
          )}
          {/* Retired WM2026 game routes — redirect instead of 404 so old links
              and any lingering PWA history entries land somewhere sensible. */}
          <Route path="/ticketshop" render={() => <Redirect to="/home" />} />
          <Route path="/ticketshopdetail/:mainpriceId" render={() => <Redirect to="/home" />} />
          <Route path="/leaderboard" render={() => <Redirect to="/home" />} />
          <Route path="/allocate" render={() => <Redirect to="/home" />} />
          <Route path="/mohrenteam" render={() => <Redirect to="/home" />} />
          <Route path="/mohrenteamdetail/:playerKey" render={() => <Redirect to="/home" />} />
          <Route path="/tipps" render={() => <Redirect to="/home" />} />
          <Route path="/create-tip" render={() => <Redirect to="/home" />} />

          <PrivateRoute path="/shipmentaddress" component={ShipmentAddress} />
          <PrivateRoute path="/orderreview" component={OrderReview} />
          <PrivateRoute path="/orderdone" component={OrderDone} />
          <Route path="*" component={NotFound} />
        </Switch>
      </Suspense>

      {window.innerWidth < 650 && <Navbar />}
      {/* {window.innerWidth >= 650 && <NavbarDesktop />} */}
    </Router>
  );
};

export default MohrenRouter;
